import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LandingNavbar } from "@/components/landing-navbar";
import { Footer } from "@/components/footer";
import { SEO, schemas } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import {
  Calculator,
  Building2,
  DollarSign,
  Clock,
  TrendingUp,
  ShieldCheck,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  HelpCircle,
  Percent,
  FileSpreadsheet,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

// ISSA 612 Standard Production Rates (Sq Ft / Hour)
const FACILITY_TYPES = [
  {
    id: "office_a",
    name: "Class A Commercial Office",
    description: "Standard executive suites, conference rooms, low-density cubicles",
    issaRate: 3000,
    defaultMargin: 40,
    icon: Building2,
  },
  {
    id: "office_medical",
    name: "Medical & Dental Clinic",
    description: "Terminal cleaning, bio-hazard sanitation, strict disinfection protocols",
    issaRate: 1800,
    defaultMargin: 45,
    icon: ShieldCheck,
  },
  {
    id: "school",
    name: "School, Daycare & University",
    description: "Classrooms, cafeterias, high-traffic corridors, heavy trash volume",
    issaRate: 3200,
    defaultMargin: 35,
    icon: Building2,
  },
  {
    id: "warehouse",
    name: "Industrial, Warehouse & Logistics",
    description: "Concrete floor scrubbing, open bays, breakrooms and locker facilities",
    issaRate: 6500,
    defaultMargin: 35,
    icon: Building2,
  },
  {
    id: "retail",
    name: "Retail Store & Showroom",
    description: "Open floor areas, display dusting, high-frequency customer restrooms",
    issaRate: 3500,
    defaultMargin: 40,
    icon: Building2,
  },
  {
    id: "fitness",
    name: "Gym & Fitness Facility",
    description: "Locker rooms, showers, equipment wipe-downs, rubber mat sanitation",
    issaRate: 2000,
    defaultMargin: 45,
    icon: ShieldCheck,
  },
];

const FREQUENCIES = [
  { id: "1x", name: "1x per week", monthlyMultiplier: 4.33, label: "Weekly" },
  { id: "2x", name: "2x per week", monthlyMultiplier: 8.67, label: "2x / week" },
  { id: "3x", name: "3x per week", monthlyMultiplier: 13.0, label: "3x / week" },
  { id: "5x", name: "5x per week (Mon-Fri)", monthlyMultiplier: 21.67, label: "5x / week" },
  { id: "7x", name: "7x per week (Daily)", monthlyMultiplier: 30.4, label: "7x / week" },
  { id: "monthly", name: "1x per month (Deep Clean)", monthlyMultiplier: 1.0, label: "Monthly" },
];

export default function BiddingCalculatorPage() {
  // Input States
  const [selectedFacility, setSelectedFacility] = useState(FACILITY_TYPES[0]);
  const [squareFootage, setSquareFootage] = useState<number>(10000);
  const [selectedFrequency, setSelectedFrequency] = useState(FREQUENCIES[3]); // 5x/week
  const [hourlyWage, setHourlyWage] = useState<number>(18);
  const [burdenRate, setBurdenRate] = useState<number>(12); // Taxes + Insurance
  const [suppliesRate, setSuppliesRate] = useState<number>(3); // 3% of revenue
  const [profitMargin, setProfitMargin] = useState<number>(40); // 40% gross margin

  // FAQ Accordion State
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  // Calculations using ISSA 612 Standard Framework
  const calculations = useMemo(() => {
    // 1. Time per Clean
    const hoursPerClean = Math.max(0.5, squareFootage / selectedFacility.issaRate);
    
    // 2. Direct Labor Cost per Clean
    const loadedHourlyWage = hourlyWage * (1 + burdenRate / 100);
    const laborCostPerClean = hoursPerClean * loadedHourlyWage;

    // 3. Monthly Labor Cost
    const monthlyCleans = selectedFrequency.monthlyMultiplier;
    const monthlyLaborCost = laborCostPerClean * monthlyCleans;

    // 4. Monthly Total Cost & Revenue Multiplier
    // Formula: Revenue = (Direct Labor Cost) / (1 - (ProfitMargin% + SuppliesRate% + Overhead%))
    const nonLaborDeduction = (profitMargin + suppliesRate + 10) / 100; // 10% operating overhead
    const effectiveDivisor = Math.max(0.2, 1 - nonLaborDeduction);

    const monthlyBidPrice = monthlyLaborCost / effectiveDivisor;
    const pricePerClean = monthlyBidPrice / monthlyCleans;
    const pricePerSqFt = monthlyBidPrice / squareFootage;

    const monthlySuppliesCost = monthlyBidPrice * (suppliesRate / 100);
    const monthlyOverhead = monthlyBidPrice * 0.10;
    const monthlyGrossProfit = monthlyBidPrice - (monthlyLaborCost + monthlySuppliesCost + monthlyOverhead);
    const monthlyHours = hoursPerClean * monthlyCleans;

    return {
      hoursPerClean: hoursPerClean.toFixed(1),
      monthlyHours: monthlyHours.toFixed(1),
      monthlyBidPrice: Math.round(monthlyBidPrice),
      pricePerClean: Math.round(pricePerClean),
      pricePerSqFt: pricePerSqFt.toFixed(3),
      monthlyLaborCost: Math.round(monthlyLaborCost),
      monthlySuppliesCost: Math.round(monthlySuppliesCost),
      monthlyGrossProfit: Math.round(monthlyGrossProfit),
      actualMargin: Math.round((monthlyGrossProfit / monthlyBidPrice) * 100),
    };
  }, [selectedFacility, squareFootage, selectedFrequency, hourlyWage, burdenRate, suppliesRate, profitMargin]);

  const faqs = [
    {
      question: "How do you calculate a commercial cleaning bid?",
      answer:
        "To calculate a commercial cleaning bid: (1) Divide cleanable square footage by ISSA standard production rates (e.g. 3,000 sq ft/hr for offices) to get hours per clean. (2) Multiply hours by your loaded wage rate (base wage + 10-15% payroll taxes/workers' comp). (3) Add 2-4% for chemical supplies and 10% overhead. (4) Divide the total cost by (1 - Target Gross Margin, typically 35-50%) to get your monthly contract price.",
    },
    {
      question: "What is the average commercial cleaning rate per square foot?",
      answer:
        "Commercial cleaning rates typically range from $0.08 to $0.22 per square foot per month for recurring 3x–5x weekly office cleaning. Medical and dental clinics range from $0.18 to $0.35 per sq ft due to strict terminal sanitization protocols. One-time commercial deep cleans range from $0.25 to $0.50 per square foot.",
    },
    {
      question: "What is the ISSA 612 standard cleaning rate?",
      answer:
        "The ISSA 612 standard is the industry benchmark published by the International Sanitary Supply Association detailing cleaning times. Standard commercial office space cleaning averages 2,500 to 3,500 sq ft/hr, medical facilities average 1,500 to 2,200 sq ft/hr, and warehouses with auto-scrubbers average 5,000 to 8,000 sq ft/hr.",
    },
    {
      question: "What profit margin should a commercial cleaning company target?",
      answer:
        "A healthy commercial cleaning business targets a Gross Profit Margin of 40% to 50% and a Net Profit Margin (EBITDA) of 20% to 30%. Operating below a 30% gross margin leaves no buffer for cleaner turnover, re-cleans, or insurance increases.",
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 selection:bg-blue-500/20 selection:text-blue-900">
      <SEO
        title="Commercial Cleaning Bid Calculator | Free Janitorial Estimator (ISSA Standards)"
        description="Free commercial cleaning bid calculator. Calculate accurate square-footage pricing, cleaning hours per shift, and profit margins based on official ISSA 612 production rate standards."
        keywords={[
          "commercial cleaning bid calculator",
          "janitorial bidding calculator",
          "cleaning rate per square foot calculator",
          "ISSA cleaning times calculator",
          "commercial cleaning pricing estimator",
        ]}
        url="/tools/commercial-cleaning-bid-calculator"
        structuredData={[
          schemas.createBreadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Free Tools", url: "/tools/commercial-cleaning-bid-calculator" },
            { name: "Commercial Cleaning Bid Calculator", url: "/tools/commercial-cleaning-bid-calculator" },
          ]),
          schemas.createFAQSchema(faqs),
          {
            "@context": "https://schema.org",
            "@type": "WebApplication",
            name: "Commercial Cleaning Bid Calculator",
            applicationCategory: "BusinessApplication",
            operatingSystem: "All",
            description:
              "Interactive commercial cleaning bid and square-footage pricing calculator powered by ISSA 612 production rate standards.",
            url: "https://os.manageclean.com/tools/commercial-cleaning-bid-calculator",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "USD",
            },
          },
        ]}
      />
      <LandingNavbar />

      {/* Hero Header */}
      <section className="relative overflow-hidden pb-12 pt-32">
        <div className="container mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-1.5 text-sm font-semibold text-blue-700 shadow-sm">
            <Calculator className="h-4 w-4" />
            <span>Official ISSA 612 Standard Estimating Engine</span>
          </div>

          <h1 className="mx-auto mt-4 max-w-4xl text-4xl font-extrabold tracking-tight text-slate-900 md:text-6xl">
            Commercial Cleaning{" "}
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Bid Calculator
            </span>
          </h1>

          <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-600">
            Stop guessing your labor hours. Calculate accurate commercial cleaning pricing, hours per clean, and protected profit margins in real-time.
          </p>
        </div>
      </section>

      {/* Main Interactive Calculator Area */}
      <section className="pb-24">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            {/* Left Column: Interactive Inputs (7 cols) */}
            <div className="space-y-6 lg:col-span-7">
              {/* Step 1: Facility Type */}
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <label className="mb-3 block text-sm font-bold uppercase tracking-wider text-slate-900">
                  1. Select Facility Type
                </label>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {FACILITY_TYPES.map((type) => {
                    const isSelected = selectedFacility.id === type.id;
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => {
                          setSelectedFacility(type);
                          setProfitMargin(type.defaultMargin);
                        }}
                        className={`flex items-start gap-3 rounded-xl border p-4 text-left transition-all ${
                          isSelected
                            ? "border-blue-600 bg-blue-50/70 ring-2 ring-blue-600"
                            : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                        }`}
                      >
                        <div
                          className={`mt-0.5 rounded-lg p-2 ${
                            isSelected ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          <Icon className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-slate-900">{type.name}</p>
                          <p className="mt-0.5 text-xs text-slate-500">
                            ISSA Rate: {type.issaRate.toLocaleString()} sq ft/hr
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Step 2: Cleanable Square Footage */}
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-bold uppercase tracking-wider text-slate-900">
                    2. Cleanable Square Footage
                  </label>
                  <div className="flex items-center rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5">
                    <input
                      type="number"
                      min="500"
                      max="500000"
                      step="500"
                      value={squareFootage}
                      onChange={(e) => setSquareFootage(Math.max(500, Number(e.target.value)))}
                      className="w-24 bg-transparent text-right font-bold text-slate-900 focus:outline-none"
                    />
                    <span className="ml-1.5 text-xs font-semibold text-slate-500">sq ft</span>
                  </div>
                </div>

                <input
                  type="range"
                  min="1000"
                  max="100000"
                  step="500"
                  value={squareFootage}
                  onChange={(e) => setSquareFootage(Number(e.target.value))}
                  className="mt-6 h-2.5 w-full cursor-pointer appearance-none rounded-lg bg-slate-200 accent-blue-600"
                />

                <div className="mt-2 flex justify-between text-xs font-medium text-slate-400">
                  <span>1,000 sq ft</span>
                  <span>25,000 sq ft</span>
                  <span>50,000 sq ft</span>
                  <span>100,000+ sq ft</span>
                </div>
              </div>

              {/* Step 3: Cleaning Frequency */}
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <label className="mb-3 block text-sm font-bold uppercase tracking-wider text-slate-900">
                  3. Service Frequency
                </label>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {FREQUENCIES.map((freq) => {
                    const isSelected = selectedFrequency.id === freq.id;
                    return (
                      <button
                        key={freq.id}
                        type="button"
                        onClick={() => setSelectedFrequency(freq)}
                        className={`rounded-xl border px-4 py-3 text-center text-sm font-bold transition-all ${
                          isSelected
                            ? "border-blue-600 bg-blue-600 text-white shadow-sm"
                            : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        {freq.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Step 4: Cost & Margin Fine-Tuning */}
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <label className="mb-4 block text-sm font-bold uppercase tracking-wider text-slate-900">
                  4. Labor Wage & Profit Margins
                </label>

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label className="text-xs font-bold text-slate-600">Base Tech Wage ($/hr)</label>
                    <div className="mt-1.5 flex items-center rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                      <span className="text-slate-400 font-bold">$</span>
                      <input
                        type="number"
                        min="10"
                        max="50"
                        value={hourlyWage}
                        onChange={(e) => setHourlyWage(Number(e.target.value))}
                        className="ml-1 w-full bg-transparent font-bold text-slate-900 focus:outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-bold text-slate-600">Target Gross Profit Margin (%)</label>
                    <div className="mt-1.5 flex items-center rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                      <input
                        type="number"
                        min="15"
                        max="70"
                        value={profitMargin}
                        onChange={(e) => setProfitMargin(Number(e.target.value))}
                        className="w-full bg-transparent font-bold text-slate-900 focus:outline-none"
                      />
                      <span className="text-slate-400 font-bold">%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Real-Time Results & Proposal CTA (5 cols) */}
            <div className="space-y-6 lg:col-span-5">
              {/* Results Card */}
              <div className="sticky top-28 rounded-3xl border border-slate-200 bg-gradient-to-b from-slate-900 via-slate-900 to-blue-950 p-8 text-white shadow-2xl">
                <div className="flex items-center justify-between border-b border-slate-800 pb-4">
                  <span className="rounded-full bg-blue-500/20 px-3 py-1 text-xs font-bold uppercase tracking-wider text-blue-400">
                    Estimated Bid Recommendation
                  </span>
                  <span className="text-xs text-slate-400">ISSA 612 Verified</span>
                </div>

                <div className="mt-6">
                  <p className="text-sm font-medium text-slate-300">Recommended Monthly Contract</p>
                  <p className="mt-1 text-5xl font-black tracking-tight text-white">
                    ${calculations.monthlyBidPrice.toLocaleString()}
                    <span className="text-xl font-medium text-slate-400">/mo</span>
                  </p>
                </div>

                {/* Key Metrics Grid */}
                <div className="mt-6 grid grid-cols-2 gap-3 border-y border-slate-800 py-6">
                  <div className="rounded-xl bg-slate-800/60 p-3.5">
                    <p className="text-xs text-slate-400">Price per Clean</p>
                    <p className="mt-1 text-xl font-bold text-white">${calculations.pricePerClean}</p>
                  </div>
                  <div className="rounded-xl bg-slate-800/60 p-3.5">
                    <p className="text-xs text-slate-400">Rate / Sq Ft</p>
                    <p className="mt-1 text-xl font-bold text-white">${calculations.pricePerSqFt}</p>
                  </div>
                  <div className="rounded-xl bg-slate-800/60 p-3.5">
                    <p className="text-xs text-slate-400">Hours per Clean</p>
                    <p className="mt-1 text-xl font-bold text-emerald-400">{calculations.hoursPerClean} hrs</p>
                  </div>
                  <div className="rounded-xl bg-slate-800/60 p-3.5">
                    <p className="text-xs text-slate-400">Monthly Hours</p>
                    <p className="mt-1 text-xl font-bold text-emerald-400">{calculations.monthlyHours} hrs</p>
                  </div>
                </div>

                {/* Profit Margin Breakdown */}
                <div className="mt-6 space-y-2 text-sm">
                  <div className="flex justify-between text-slate-300">
                    <span>Direct Labor & Burden:</span>
                    <span className="font-semibold text-white">${calculations.monthlyLaborCost.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-slate-300">
                    <span>Chemicals & Supplies (3%):</span>
                    <span className="font-semibold text-white">${calculations.monthlySuppliesCost.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-emerald-400 font-bold pt-2 border-t border-slate-800">
                    <span>Target Monthly Profit:</span>
                    <span>${calculations.monthlyGrossProfit.toLocaleString()} ({calculations.actualMargin}%)</span>
                  </div>
                </div>

                {/* Conversion CTA */}
                <div className="mt-8">
                  <Link to="/sign-up">
                    <Button size="lg" className="w-full bg-blue-600 py-6 text-base font-bold text-white shadow-lg hover:bg-blue-500">
                      Generate Full AI Proposal in ManageClean <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                  <p className="mt-3 text-center text-xs text-slate-400">
                    Includes electronic signing, scope matrix & PDF exports. Free forever plan available.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Educational Walkthrough & FAQ Section for AEO */}
      <section className="border-t border-slate-200 bg-white py-20">
        <div className="container mx-auto max-w-4xl px-4">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-slate-900 md:text-4xl">
              How Commercial Cleaning Bids are Calculated
            </h2>
            <p className="mt-3 text-lg text-slate-600">
              Understanding the mathematics behind profitable janitorial contracting.
            </p>
          </div>

          <div className="mt-12 space-y-8 text-slate-700 leading-relaxed">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
              <h3 className="text-xl font-bold text-slate-900">The 4-Step Bidding Formula</h3>
              <p className="mt-2">
                1. <strong>Cleanable Area:</strong> Measure the true cleanable square footage (excluding structural dead space).
              </p>
              <p className="mt-1">
                2. <strong>Labor Hours:</strong> Cleanable Sq Ft ÷ ISSA Production Rate = Cleaning Hours per Shift.
              </p>
              <p className="mt-1">
                3. <strong>Burdened Labor:</strong> Hours × Hourly Wage × (1 + Payroll Tax / Insurance Burden).
              </p>
              <p className="mt-1">
                4. <strong>Gross Profit Divisor:</strong> Total Direct Costs ÷ (1 - Target Gross Margin) = Monthly Contract Price.
              </p>
            </div>

            {/* FAQs */}
            <div className="space-y-4 pt-8">
              <h3 className="text-2xl font-bold text-slate-900">Frequently Asked Questions</h3>
              {faqs.map((faq, index) => {
                const isOpen = expandedFaq === index;
                return (
                  <div key={index} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                    <button
                      onClick={() => setExpandedFaq(isOpen ? null : index)}
                      className="flex w-full items-center justify-between p-5 text-left font-bold text-slate-900 hover:bg-slate-50"
                    >
                      <span>{faq.question}</span>
                      {isOpen ? <ChevronUp className="h-5 w-5 text-slate-500" /> : <ChevronDown className="h-5 w-5 text-slate-500" />}
                    </button>
                    {isOpen && (
                      <div className="border-t border-slate-100 bg-slate-50/50 p-5 text-slate-600 leading-relaxed">
                        {faq.answer}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Bottom Conversion Banner */}
          <div className="mt-16 rounded-3xl bg-gradient-to-r from-blue-600 to-indigo-700 p-8 text-center text-white shadow-xl">
            <h3 className="text-2xl font-bold md:text-3xl">Turn Your Estimates Into Signed Contracts</h3>
            <p className="mx-auto mt-2 max-w-xl text-blue-100">
              ManageClean OS automatically turns your calculator inputs into legally binding PDF proposals with integrated e-signatures.
            </p>
            <div className="mt-6 flex justify-center gap-4">
              <Link to="/sign-up">
                <Button size="lg" className="bg-white font-bold text-blue-900 hover:bg-blue-50">
                  Get Started Free <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
